import moment from "moment";

/** Canonical display: 30 Aug 2026, 11:44:32 AM */
export const DATE_TIME_FORMAT = "DD MMM YYYY, hh:mm:ss A";

/** Date-only labels (filters, calendar-day fields): 30 Aug 2026 */
export const DATE_FORMAT = "DD MMM YYYY";

/**
 * Parse app date values into a moment (local timezone).
 * Supports: unix seconds, unix ms, Date, ISO / parseable strings, YYYY-MM-DD.
 */
export function parseAppDate(value) {
  if (value == null || value === "" || value === "-") return null;

  if (moment.isMoment(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? m : null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const m = value > 1e12 ? moment(value) : moment.unix(value);
    return m.isValid() ? m : null;
  }

  const s = String(value).trim();
  if (!s) return null;

  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s);
    const m = n > 1e12 ? moment(n) : moment.unix(n);
    return m.isValid() ? m : null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const m = moment(s, "YYYY-MM-DD", true);
    return m.isValid() ? m : null;
  }

  const m = moment(s);
  return m.isValid() ? m : null;
}

/**
 * Format any stored/API date as local Date + Exact Time.
 * Returns "—" when value is missing/invalid (safe for tables).
 */
export function formatDateTime(value, fallback = "—") {
  const m = parseAppDate(value);
  if (!m) return fallback;
  return m.format(DATE_TIME_FORMAT);
}

/**
 * Format as local calendar date only (no time).
 * Use for day-close business dates, range chips, etc.
 */
export function formatDate(value, fallback = "—") {
  const m = parseAppDate(value);
  if (!m) return fallback;
  return m.format(DATE_FORMAT);
}

/**
 * Local calendar YYYY-MM-DD for <input type="date">.
 * Avoids UTC day-shift from toISOString().
 */
export function toLocalYmd(value = new Date()) {
  const m = parseAppDate(value) || moment();
  return m.format("YYYY-MM-DD");
}

/**
 * Local YYYY-MM-DDTHH:mm for <input type="datetime-local">.
 */
export function toLocalDateTimeInput(value = new Date()) {
  const m = parseAppDate(value) || moment();
  return m.format("YYYY-MM-DDTHH:mm");
}

/**
 * Convert form/API date input to unix seconds (mirrors backend toUnixSeconds).
 * datetime-local strings are parsed as local wall-clock (never UTC).
 */
export function toUnixSeconds(value) {
  if (value == null || value === "") {
    return Math.floor(Date.now() / 1000);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, month, d] = s.split("-").map(Number);
    const now = new Date();
    return Math.floor(
      new Date(
        y,
        month - 1,
        d,
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      ).getTime() / 1000
    );
  }
  // YYYY-MM-DDTHH:mm[:ss] — local components (fixes Vercel UTC day-shift)
  const dt = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (dt) {
    const y = Number(dt[1]);
    const month = Number(dt[2]);
    const d = Number(dt[3]);
    const h = Number(dt[4]);
    const mi = Number(dt[5]);
    const se = Number(dt[6] || 0);
    return Math.floor(new Date(y, month - 1, d, h, mi, se).getTime() / 1000);
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) {
    return Math.floor(Date.now() / 1000);
  }
  return Math.floor(parsed.getTime() / 1000);
}

export default formatDateTime;
